import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, api } from "../App";
import { ArrowLeft, Link2, Copy, Check, Users, Zap, Gift, Trophy, Share2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Referral() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [applyCode, setApplyCode] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    api.get("/referrals/stats").then(res => {
      setStats(res.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const copyCode = () => {
    if (stats?.code) {
      navigator.clipboard.writeText(stats.code);
      setCopied(true);
      toast.success("Referral code copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyLink = () => {
    if (stats?.share_url) {
      navigator.clipboard.writeText(stats.share_url);
      toast.success("Referral link copied!");
    }
  };

  const handleApply = async () => {
    if (!applyCode.trim()) return;
    setApplying(true);
    try {
      const res = await api.post("/referrals/apply", { referral_code: applyCode });
      toast.success(res.data.message);
      setApplyCode("");
      const s = await api.get("/referrals/stats");
      setStats(s.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply code");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="referral-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white" data-testid="referral-back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">REFER & <span className="text-[#F7931A]">EARN</span></h1>
          <Link2 className="text-[#F7931A]" size={20} />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Referral Code Card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="card-dark p-6 border border-[#F7931A]/20">
          <p className="text-zinc-400 text-sm mb-2">Your Referral Code</p>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 bg-[#0A0A0A] rounded-xl px-5 py-3 border border-[#F7931A]/30">
              <p className="text-[#F7931A] font-mono font-bold text-2xl tracking-wider" data-testid="referral-code">
                {stats?.code || "Loading..."}
              </p>
            </div>
            <Button onClick={copyCode} variant="outline"
              className="border-[#F7931A]/30 text-[#F7931A] hover:bg-[#F7931A]/10 px-4" data-testid="copy-code-btn">
              {copied ? <Check size={18} /> : <Copy size={18} />}
            </Button>
          </div>
          <button onClick={copyLink}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-[#F7931A]/10 text-[#F7931A] text-sm hover:bg-[#F7931A]/20 transition-all"
            data-testid="copy-link-btn">
            <Share2 size={14} /> Copy Share Link
          </button>
        </motion.div>

        {/* Stats */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-3">
          <div className="card-dark p-4 text-center" data-testid="referral-count">
            <Users className="text-blue-400 mx-auto mb-2" size={20} />
            <p className="text-white font-bold text-xl">{stats?.referral_count || 0}</p>
            <p className="text-zinc-500 text-xs">Referrals</p>
          </div>
          <div className="card-dark p-4 text-center" data-testid="referral-sats">
            <Zap className="text-[#F7931A] mx-auto mb-2" size={20} />
            <p className="text-white font-bold text-xl">{(stats?.total_sats_earned || 0).toLocaleString()}</p>
            <p className="text-zinc-500 text-xs">BTC XP Earned</p>
          </div>
          <div className="card-dark p-4 text-center">
            <Gift className="text-green-400 mx-auto mb-2" size={20} />
            <p className="text-white font-bold text-xl">{stats?.next_reward || 210}</p>
            <p className="text-zinc-500 text-xs">Next Reward</p>
          </div>
        </motion.div>

        {/* How It Works */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="card-dark p-5">
          <h3 className="text-white font-medium mb-3">How It Works</h3>
          <div className="space-y-3">
            {[
              { step: "1", text: "Share your referral code with friends and family" },
              { step: "2", text: "They sign up and enter your code" },
              { step: "3", text: `You earn ${stats?.next_reward || 2100} BTC XP per referral. They get 1,000 bonus BTC XP.` },
              { step: "4", text: "Hit milestones for bonus BTC XP rewards" },
            ].map(s => (
              <div key={s.step} className="flex gap-3 items-start">
                <div className="w-7 h-7 rounded-full bg-[#F7931A]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#F7931A] font-mono text-xs font-bold">{s.step}</span>
                </div>
                <p className="text-zinc-400 text-sm">{s.text}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Milestones */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="card-dark p-5">
          <h3 className="text-white font-medium mb-3 flex items-center gap-2">
            <Trophy className="text-[#F7931A]" size={16} /> Milestones
          </h3>
          <div className="space-y-2">
            {stats?.milestones?.map((m, i) => (
              <div key={i} className={`flex justify-between items-center p-3 rounded-lg ${m.reached ? "bg-[#F7931A]/10 border border-[#F7931A]/30" : "bg-[#0A0A0A] border border-white/5"}`}
                data-testid={`milestone-${m.target}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${m.reached ? "bg-[#F7931A]" : "bg-zinc-700"}`} />
                  <div>
                    <p className={`text-sm font-medium ${m.reached ? "text-[#F7931A]" : "text-zinc-400"}`}>{m.label}</p>
                    <p className="text-zinc-600 text-xs">{m.target} referrals</p>
                  </div>
                </div>
                <span className={`font-mono text-sm ${m.reached ? "text-[#F7931A]" : "text-zinc-600"}`}>+{m.reward.toLocaleString()} BTC XP</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Apply Code */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="card-dark p-5">
          <h3 className="text-white font-medium mb-3">Have a Referral Code?</h3>
          <div className="flex gap-2">
            <input type="text" value={applyCode} onChange={e => setApplyCode(e.target.value.toUpperCase())}
              placeholder="Enter referral code" className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2 text-white placeholder-zinc-600 focus:border-[#F7931A]/50 focus:outline-none font-mono"
              data-testid="apply-code-input" />
            <Button onClick={handleApply} disabled={applying || !applyCode.trim()}
              className="bg-[#F7931A] hover:bg-[#F7931A]/90 text-black" data-testid="apply-code-btn">
              {applying ? "..." : "Apply"}
            </Button>
          </div>
        </motion.div>

        {/* Recent Referrals */}
        {stats?.recent_referrals?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="card-dark p-5">
            <h3 className="text-white font-medium mb-3">Recent Referrals</h3>
            <div className="space-y-2">
              {stats.recent_referrals.map((r, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                  <div>
                    <p className="text-white text-sm">{r.referred_name}</p>
                    <p className="text-zinc-600 text-xs">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="text-[#F7931A] font-mono text-sm">+{r.sats_awarded} BTC XP</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
